import { FC, useCallback, SetStateAction, Dispatch } from 'react';
import { Box, Typography, CircularProgress, List, ListItem, ListItemIcon, ListItemText, Link } from '@mui/material';
import { grey } from '@mui/material/colors';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import InfiniteScroll from 'react-infinite-scroll-component';

import { Post } from '@libs/models';
import { toHumanArray } from '@libs/utils';
import { JobSearchPost } from '@carcareers/pages';

interface JobseekerPostInfiniteScrollProps {
  numPosts: number;
  hasMore: boolean;
  posts: Record<string, JobSearchPost>;
  selectedPost: JobSearchPost | null;
  getPosts: () => Promise<void>;
  setSelectedPost: Dispatch<SetStateAction<JobSearchPost | null>>;
}

export const JobseekerPostInfiniteScroll: FC<JobseekerPostInfiniteScrollProps> = ({
  posts,
  numPosts,
  hasMore,
  selectedPost,
  getPosts,
  setSelectedPost,
}) => {
  const handleSelectPost = useCallback(
    (post: Post) => {
      setSelectedPost(post);
    },
    [setSelectedPost],
  );

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        height: '100%',
        maxHeight: { xs: 'calc(100vh - 64px)', md: 'calc(100vh - 64px)' },
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '2px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(0,0,0,0.5)',
        },
      }}
      id="scrollableDiv"
    >
      <InfiniteScroll
        dataLength={numPosts}
        next={getPosts}
        hasMore={hasMore}
        scrollableTarget="scrollableDiv"
        height={`calc(100vh - 136px)`}
        loader={
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        }
        endMessage={
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Typography component="div">No more jobs found.</Typography>
          </Box>
        }
      >
        {Object.values(posts).map((post) => {
          const isConfidential = post.job?.rooftop?.isConfidential;

          return (
            <Box
              key={post.id}
              sx={{
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                borderLeft: '4px solid transparent',
                borderLeftColor: post.id === selectedPost?.id ? '#2196f3' : 'transparent',
                transition: 'all 0.1s ease',
                '&:hover': {
                  borderLeft: '4px solid #2196f3',
                  cursor: 'pointer',
                },
              }}
              onClick={() => handleSelectPost(post)}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography variant="h3">{post.job?.title}</Typography>
                {post.applied && <CheckCircleIcon color="primary" />}
                {post.saved && !post.applied && <BookmarkIcon color="secondary" />}
              </Box>
              {isConfidential ? (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: '600',
                    padding: '4px 8px',
                    marginLeft: '-8px',
                    width: 'fit-content',
                    borderRadius: '4px',
                    color: 'text.secondary',
                  }}
                >
                  Confidential
                </Typography>
              ) : (
                <Link
                  href={`/rooftop/${post.job?.rooftop?.id}`}
                  sx={{ textDecoration: 'none' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: '600',
                      padding: '4px 8px',
                      marginLeft: '-8px',
                      width: 'fit-content',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: grey['100'],
                      },
                    }}
                  >
                    {post.job?.rooftop?.name}
                  </Typography>
                </Link>
              )}
              <List dense disablePadding sx={{ fontSize: '0.875rem', mt: 2 }}>
                <ListItem disablePadding>
                  <ListItemIcon>
                    <LocationOnIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${post.job?.rooftop?.location}`}
                    primaryTypographyProps={{ fontSize: 'inherit' }}
                  />
                </ListItem>
                {!isConfidential && (
                  <ListItem disablePadding>
                    <ListItemIcon>
                      <WorkIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${post.job?.rooftop?.name}`}
                      primaryTypographyProps={{ fontSize: 'inherit' }}
                    />
                  </ListItem>
                )}
                <ListItem disablePadding>
                  <ListItemIcon>
                    <CardGiftcardIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={toHumanArray('camel', post.job?.benefits || [], true).join(', ')}
                    primaryTypographyProps={{ fontSize: 'inherit' }}
                  />
                </ListItem>
              </List>
            </Box>
          );
        })}
      </InfiniteScroll>
    </Box>
  );
};

export default JobseekerPostInfiniteScroll;
